import React, { useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Button, TextField, IconButton, LinearProgress,
    Autocomplete, Snackbar, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Webcam from 'react-webcam';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Slide from '@mui/material/Slide';
import { reportsModel } from '../Axios/AxiosConfig';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ─── helper: determine filetype from mime ─── */
const getFileCategory = (file) => {
    if (!file || !file.type) return 'unknown';
    if (file.type === 'application/pdf') return 'pdf';
    if (file.type.startsWith('image/')) return 'images';
    return 'unknown';
};

/* ─── styles ─── */
const styles = {
    page: {
        minHeight: '100vh',
        backgroundColor: '#F8F7F4',
        fontFamily: "'Poppins', sans-serif",
    },
    nav: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        padding: '1rem 1.5rem',
        background: 'linear-gradient(135deg, #75623F 0%, #5a4a2f 100%)',
        color: 'var(--themeSurfaceColor, #FDFDFD)',
        boxShadow: '0 2px 8px rgba(117,98,63,0.25)',
    },
    navTitle: {
        margin: 0,
        fontFamily: "'Libre Baskerville', serif",
        fontWeight: 400,
        fontSize: '22px',
        letterSpacing: '0.5px',
    },
    container: {
        maxWidth: '550px',
        margin: '0 auto',
        padding: '1.5rem 1rem 3rem',
    },
    card: {
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        marginBottom: '1rem',
    },
    sectionLabel: {
        margin: '0 0 0.75rem 0',
        fontWeight: 600,
        fontSize: '15px',
        color: 'var(--themePrimaryColor, #3B362A)',
    },
    dropZone: {
        border: '2px dashed var(--themeSecondaryColor, #C0B7A7)',
        borderRadius: '12px',
        padding: '2rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        cursor: 'pointer',
        backgroundColor: '#FDFCF9',
        transition: 'border-color 0.2s, background-color 0.2s',
    },
    dropZoneHover: {
        borderColor: 'var(--themePrimaryColor, #DA6E0B)',
        backgroundColor: '#FFF8F2',
    },
    dropIcon: {
        fontSize: '48px',
        color: 'var(--themeSecondaryColor, #C0B7A7)',
    },
    dropText: {
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 500,
        fontSize: '14px',
        color: 'var(--themePrimaryColor, #312110)',
        margin: 0,
    },
    dropSubtext: {
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 400,
        fontSize: '12px',
        color: '#999',
        margin: 0,
    },
    divider: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        margin: '1rem 0',
    },
    dividerLine: {
        flex: 1,
        height: '1px',
        backgroundColor: '#E0DDD6',
    },
    dividerText: {
        fontWeight: 500,
        fontSize: '12px',
        color: '#999',
        margin: 0,
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        backgroundColor: '#FDFCF9',
        borderRadius: '8px',
        border: '1px solid #ECECE2',
        marginBottom: '0.5rem',
    },
    fileIcon: {
        color: 'var(--themePrimaryColor, #DA6E0B)',
        fontSize: '28px',
    },
    fileName: {
        flex: 1,
        margin: 0,
        fontSize: '13px',
        fontWeight: 500,
        color: 'var(--themePrimaryColor, #3B362A)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fileSize: {
        margin: 0,
        fontSize: '11px',
        color: '#999',
    },
    progressContainer: {
        marginBottom: '1rem',
    },
    progressLabel: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '12px',
        color: 'var(--themePrimaryColor, #312110)',
        fontWeight: 500,
        marginBottom: '4px',
    },
    uploadBtn: {
        width: '100%',
        textTransform: 'none',
        backgroundColor: 'var(--themePrimaryColor, #DA6E0B)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 600,
        fontSize: '16px',
        padding: '14px',
        boxShadow: '0 2px 8px rgba(218,110,11,0.3)',
    },
    cameraBtn: {
        width: '100%',
        textTransform: 'none',
        backgroundcolor: 'var(--themePrimaryColor, #312110)',
        color: 'var(--themeSurfaceColor, #FDFDFD)',
        borderRadius: '10px',
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 500,
        padding: '12px',
        gap: '8px',
    },
    successContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem 2rem',
        textAlign: 'center',
    },
    successIcon: {
        fontSize: '64px',
        color: '#2e7d32',
        marginBottom: '1rem',
    },
    successTitle: {
        color: '#2e7d32',
        fontWeight: 600,
        fontSize: '20px',
        fontFamily: "'Poppins', sans-serif",
        margin: '0 0 0.5rem 0',
    },
    successSubtitle: {
        color: '#625e55',
        fontFamily: "'Poppins', sans-serif",
        fontSize: '14px',
        margin: '0 0 1.5rem 0',
    },
};

const PatientUploadReport = () => {
    const { id } = useParams();
    const [reportType, setReportType] = useState('');
    const [report, setReport] = useState('');
    const [allReports, setReports] = useState([]);
    const webcamRef = useRef(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [dragOver, setDragOver] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    const [isUploading, setIsUploading] = useState(false);
    const fileInput = useRef();
    const [open, setOpen] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const videoConstraints = {
        width: 1280,
        height: 720,
        facingMode: 'environment',
    };

    /* ─── file handling ─── */
    const handleFileUpload = async (e) => {
        e.preventDefault();
        const maxFileSize = 40 * 1024 * 1024;
        const files = e.target.files || e.dataTransfer?.files;
        if (!files) return;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const category = getFileCategory(file);

            if (category === 'unknown') {
                setSnackbar({
                    open: true,
                    message: `"${file.name}" is not a supported file type. Please upload PDF or image files.`,
                    severity: 'warning'
                });
                continue;
            }

            if (file.size > maxFileSize) {
                setSnackbar({
                    open: true,
                    message: `"${file.name}" exceeds the 40MB limit.`,
                    severity: 'warning'
                });
                continue;
            }

            try {
                const filedata = await convertToBase64(file);
                setReports((prev) => [
                    ...prev,
                    {
                        file: filedata,
                        fileName: file.name,
                        fileType: category,       // 'pdf' or 'images'
                        fileSize: file.size,
                    },
                ]);
            } catch (error) {
                console.error("Error reading file:", error);
                setSnackbar({
                    open: true,
                    message: `Failed to read "${file.name}". Please try again.`,
                    severity: 'error'
                });
            }
        }

        // Reset the file input so the same file can be re-selected
        if (fileInput.current) fileInput.current.value = '';
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
                const base64String = fileReader.result.replace('data:', '').replace(/^.+,/, '');
                resolve(base64String);
            };
            fileReader.onerror = (error) => reject(error);
        });
    };

    const handleDelete = (index) => {
        setReports((prev) => prev.filter((_, i) => i !== index));
    };

    const capture = useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot().replace('data:', '').replace(/^.+,/, '');
        setReports((prev) => [
            ...prev,
            {
                file: imageSrc,
                fileName: `Photo_${Date.now()}.jpg`,
                fileType: 'images',
                fileSize: 0,
            },
        ]);
        handleClose();
    }, [webcamRef]);

    /* ─── drag and drop ─── */
    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };
    const handleDragLeave = () => setDragOver(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFileUpload(e);
    };

    /* ─── upload ─── */
    const UploadReport = async () => {
        if (!report) {
            setSnackbar({ open: true, message: 'Please select a report type.', severity: 'warning' });
            return;
        }
        if (allReports.length === 0) {
            setSnackbar({ open: true, message: 'Please add at least one file.', severity: 'warning' });
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Determine the dominant file type from uploaded files
            const hasImages = allReports.some(r => r.fileType === 'images');
            const hasPdf = allReports.some(r => r.fileType === 'pdf');
            const filetype = hasPdf ? 'pdf' : 'images';

            const formData = {
                reports: allReports,
                ReportId: Date.now().toString(),
                ReportUploadedby: id,
                ReportType: report || `Report_${Date.now()}`,
                appointmentId: '',
                CreatedAt: new Date().toString(),
                UID: id,
                filetype: filetype,
                Comments: 'This Report is uploaded by Patient',
            };

            const config = {
                headers: { 'Content-Type': 'application/json' },
                onUploadProgress: (progressEvent) => {
                    const { loaded, total } = progressEvent;
                    const percent = Math.round((loaded * 100) / total);
                    setUploadProgress(percent);
                },
            };

            const res = await reportsModel.post('/uploadreportsAsyncWithAI', formData, config);

            if (res?.status === 200 && (res?.data?.Reports?.length > 0 || res?.data?.extractionStatus === 'pending')) {
                setSnackbar({ open: true, message: 'Report uploaded successfully!', severity: 'success' });
                setReports([]);
                setReportType('');
                setReport('');
                setUploadSuccess(true);
            } else {
                throw new Error('Upload failed. Please try again.');
            }
        } catch (err) {
            console.error('Upload error:', err);
            let errorMessage = 'Failed to upload report. Please try again.';
            if (err.response?.data?.message) errorMessage = err.response.data.message;
            else if (err.message) errorMessage = err.message;
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const reportOptions = [
        "BCA", "ECG", "Blood Test", "Urine Test", "X-ray", "MRI Scan", "CT Scan",
        "Ultrasound", "PET Scan", "EEG (Electroencephalogram)", "EMG (Electromyography)",
        "Endoscopy", "Colonoscopy", "Liver Function Test (LFT)", "Kidney Function Test (KFT)",
        "Lipid Profile", "Thyroid Function Test", "CBC (Complete Blood Count)",
        "Blood Sugar Test", "Allergy Test", "Pathology Report", "Microbiology Report",
        "Radiology Report", "Histopathology Report", "Genetic Test", "Hormone Test",
        "Biopsy Report", "Cardiac Stress Test", "Pulmonary Function Test",
        "Audiometry (Hearing Test)", "Vision Test", "Bone Density Test", "Drug Test",
        "Toxicology Report", "Vaccination Record", "Prescription", "Surgical Report",
        "Medical Discharge Summary", "Health Checkup Report",
    ];

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    /* ─── render ─── */
    return (
        <>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}
                    variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <div style={styles.page}>
                {/* ─── Nav ─── */}
                <nav style={styles.nav}>
                    <img src='/icons/Dhanwanthari_Logo copy.png' alt='logo'
                        style={{ width: '2.5rem' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <p style={styles.navTitle}>Upload Reports</p>
                </nav>

                <div style={styles.container}>

                    {/* ─── Success State ─── */}
                    {uploadSuccess && allReports.length === 0 ? (
                        <div style={{ ...styles.card, ...styles.successContainer }}>
                            <CheckCircleOutlineIcon style={styles.successIcon} />
                            <p style={styles.successTitle}>Uploaded Successfully!</p>
                            <p style={styles.successSubtitle}>
                                Your report has been submitted and is being processed.
                            </p>
                            <Button
                                style={{
                                    ...styles.cameraBtn,
                                    width: 'auto',
                                    padding: '10px 32px',
                                }}
                                onClick={() => setUploadSuccess(false)}
                            >
                                Upload Another
                            </Button>
                        </div>
                    ) : (
                        <>
                            {/* ─── Report Type ─── */}
                            <div style={styles.card}>
                                <p style={styles.sectionLabel}>Report Type</p>
                                <Autocomplete
                                    freeSolo
                                    options={reportOptions}
                                    inputValue={reportType}
                                    onInputChange={(_, v) => { setReportType(v); setReport(v); }}
                                    onChange={(_, v) => { setReportType(v ?? ''); setReport(v ?? ''); }}
                                    clearOnBlur={false}
                                    size="small"
                                    sx={{
                                        backgroundColor: "#ECECE2",
                                        borderRadius: "8px",
                                        '& .MuiOutlinedInput-root': {
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: 500,
                                            '&.Mui-focused fieldset': { borderColor: 'var(--themePrimaryColor, #DA6E0B)' },
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                            placeholder="Select or type report name"
                                            variant="outlined"
                                        />
                                    )}
                                />
                            </div>

                            {/* ─── File Upload ─── */}
                            <div style={styles.card}>
                                <p style={styles.sectionLabel}>Upload Files</p>

                                <label htmlFor='file-input' style={{ display: 'block' }}>
                                    <div
                                        style={{
                                            ...styles.dropZone,
                                            ...(dragOver ? styles.dropZoneHover : {}),
                                        }}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <CloudUploadIcon style={{
                                            ...styles.dropIcon,
                                            color: dragOver ? 'var(--themePrimaryColor, #DA6E0B)' : 'var(--themeSecondaryColor, #C0B7A7)',
                                        }} />
                                        <p style={styles.dropText}>
                                            {dragOver ? 'Drop files here' : 'Tap to choose files'}
                                        </p>
                                        <p style={styles.dropSubtext}>
                                            PDF or Images • Max 40 MB per file
                                        </p>
                                    </div>
                                    <input
                                        hidden
                                        type='file'
                                        id='file-input'
                                        ref={fileInput}
                                        multiple
                                        onChange={handleFileUpload}
                                        accept="image/png,image/gif,image/jpeg,image/webp,application/pdf"
                                    />
                                </label>

                                {/* Camera button */}
                                <div style={styles.divider}>
                                    <div style={styles.dividerLine} />
                                    <p style={styles.dividerText}>or</p>
                                    <div style={styles.dividerLine} />
                                </div>
                                <Button style={styles.cameraBtn} onClick={handleClickOpen}>
                                    <CameraAltIcon style={{ fontSize: '20px' }} />
                                    Take a Photo
                                </Button>
                            </div>

                            {/* ─── Selected Files ─── */}
                            {allReports.length > 0 && (
                                <div style={styles.card}>
                                    <p style={styles.sectionLabel}>
                                        Selected Files ({allReports.length})
                                    </p>

                                    {allReports.map((item, i) => (
                                        <div style={styles.fileItem} key={i}>
                                            {item.fileType === 'pdf'
                                                ? <PictureAsPdfIcon style={{ ...styles.fileIcon, color: '#c62828' }} />
                                                : <ImageIcon style={styles.fileIcon} />
                                            }
                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                <p style={styles.fileName}>{item.fileName}</p>
                                                {item.fileSize > 0 && (
                                                    <p style={styles.fileSize}>{formatFileSize(item.fileSize)}</p>
                                                )}
                                            </div>
                                            <IconButton onClick={() => handleDelete(i)} size="small"
                                                sx={{ color: '#999', '&:hover': { color: '#c62828' } }}>
                                                <DeleteIcon fontSize='small' />
                                            </IconButton>
                                        </div>
                                    ))}

                                    {/* Progress */}
                                    {isUploading && (
                                        <div style={styles.progressContainer}>
                                            <div style={styles.progressLabel}>
                                                <span>Uploading…</span>
                                                <span>{uploadProgress}%</span>
                                            </div>
                                            <LinearProgress
                                                variant='determinate'
                                                value={uploadProgress}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    backgroundColor: '#f0e5d6',
                                                    '& .MuiLinearProgress-bar': { backgroundColor: 'var(--themePrimaryColor, #DA6E0B)' },
                                                }}
                                            />
                                        </div>
                                    )}

                                    <Button
                                        style={{
                                            ...styles.uploadBtn,
                                            ...(isUploading ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                                        }}
                                        disabled={isUploading}
                                        onClick={UploadReport}
                                    >
                                        {isUploading ? 'Uploading…' : 'Upload Report'}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ─── Camera Dialog ─── */}
                <Dialog
                    open={open}
                    TransitionComponent={Transition}
                    keepMounted
                    onClose={handleClose}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogContent sx={{ p: 0 }}>
                        {open && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '1rem',
                                gap: '1rem',
                            }}>
                                <Webcam
                                    audio={false}
                                    ref={webcamRef}
                                    screenshotFormat='image/jpeg'
                                    videoConstraints={videoConstraints}
                                    style={{ width: '100%', borderRadius: '8px' }}
                                />
                                <div style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    width: '100%',
                                }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={handleClose}
                                        sx={{
                                            color: 'var(--themePrimaryColor, #312110)',
                                            bordercolor: 'var(--themePrimaryColor, #312110)',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: '8px',
                                        }}
                                    >Cancel</Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={capture}
                                        sx={{
                                            backgroundColor: 'var(--themePrimaryColor, #DA6E0B)',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            '&:hover': { backgroundColor: '#b55a08' },
                                        }}
                                    >Capture</Button>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
};

export default PatientUploadReport;
